// Step-2 (Campaign setup) geo-mode advance gating, extracted from
// SelfServiceWizard.handleNext so the branching is testable without
// mounting the whole wizard.
//
// Outcomes the wizard's caller acts on:
//   - 'advance'         → no geo conflict, proceed to step 3
//   - 'conflict-modal'  → custom + 2+ tabs hold data; open the
//                          conflict modal so the buyer commits to one
//
// `advance` may also carry `correctedGeoMode`, asking the wizard to
// realign form state's geoMode to whichever tab the buyer's data lives
// on. That covers a real failure mode: buyer picks States, then clicks
// the DMAs tab (geoMode flips to 'dmas') without selecting DMAs, then
// advances — geoMode says 'dmas' but the states list is the only thing
// populated. Without realignment the API serializer would read the empty
// DMAs list and silently drop the states selection.
//
// CTV-533: the empty-custom-error branch moved into the zod resolver
// (campaignSetupSchema.superRefine) so it auto-clears the moment the
// buyer picks a state/DMA/zip. See ADR-0012.
//
// Default-mode submissions always advance — the conflict check only
// runs when targetingMode === 'custom'.
//
// CTV-502: the standalone ZIPs tab was removed; the `unsaved-zips-warning`
// outcome and its accompanying buffer-check are gone with it.

import type { SelfServiceFormData } from '../../types/selfService';

type GeoTab = NonNullable<SelfServiceFormData['geoMode']>;

export type Step2GeoOutcome =
  | { kind: 'advance'; correctedGeoMode?: GeoTab }
  | { kind: 'conflict-modal' };

interface Step2GeoInput {
  targetingMode: SelfServiceFormData['targetingMode'];
  geoMode: SelfServiceFormData['geoMode'];
  geoTargetZips: SelfServiceFormData['geoTargetZips'];
  selectedStates: SelfServiceFormData['selectedStates'];
  selectedDMAs: SelfServiceFormData['selectedDMAs'];
}

export function step2GeoOutcome({
  targetingMode,
  geoMode,
  geoTargetZips,
  selectedStates,
  selectedDMAs,
}: Step2GeoInput): Step2GeoOutcome {
  if (targetingMode !== 'custom') return { kind: 'advance' };
  const populated: GeoTab[] = [];
  if ((geoTargetZips?.length ?? 0) > 0) populated.push('radius');
  if ((selectedStates?.length ?? 0) > 0) populated.push('states');
  if ((selectedDMAs?.length ?? 0) > 0) populated.push('dmas');
  if (populated.length === 0) {
    // Empty-custom is now blocked by the resolver in campaignSetupSchema;
    // by the time we get here, the buyer cleared that gate. Treat as
    // advance (no-op realignment).
    return { kind: 'advance' };
  }
  if (populated.length > 1) {
    return { kind: 'conflict-modal' };
  }
  // Exactly one tab has data — make sure geoMode points at it so the
  // serializer reads the right list. Realignment is silent: the buyer
  // only filled in one place, so there's nothing ambiguous to surface.
  const only = populated[0];
  return geoMode === only ? { kind: 'advance' } : { kind: 'advance', correctedGeoMode: only };
}
